import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { apiRequest } from "../utils/http";
import {
  canCreateUsers,
  canDeleteUsers,
  canEditUsers,
  canSuspendUsers,
  getAdminUser,
  isSuperAdmin
} from "../utils/auth";

const initialEditState = {
  id: "",
  fullName: "",
  email: "",
  phone: "",
  role: "customer",
  status: "active",
  suspendedReason: ""
};

const initialCreateState = {
  fullName: "",
  email: "",
  phone: "",
  password: "",
  status: "active"
};

const UsersPage = () => {
  const adminUser = getAdminUser();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(initialEditState);
  const [createForm, setCreateForm] = useState(initialCreateState);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const loadUsers = async () => {
    try {
      const response = await apiRequest("/api/admin/users?role=customer");
      setUsers(response?.data || []);
    } catch (loadError) {
      setError(loadError.message || "Unable to load users.");
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const activeCount = useMemo(() => users.filter((user) => user.status === "active").length, [users]);

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      id: user.id,
      fullName: user.fullName || "",
      email: user.email || "",
      phone: user.phone || "",
      role: user.role || "customer",
      status: user.status || "active",
      suspendedReason: user.suspendedReason || ""
    });
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const handleCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((current) => ({ ...current, [name]: value }));
  };

  const handleCreateUser = async () => {
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest("/api/admin/users", {
        method: "POST",
        body: JSON.stringify(createForm)
      });
      setSuccess(response?.message || "User created successfully.");
      setCreateForm(initialCreateState);
      setIsCreateDialogOpen(false);
      await loadUsers();
    } catch (createError) {
      setError(createError.message || "Unable to create user.");
    }
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/admin/users/${editForm.id}`, {
        method: "PATCH",
        body: JSON.stringify(editForm)
      });
      setSuccess(response?.message || "User updated successfully.");
      setEditingUser(null);
      await loadUsers();
    } catch (saveError) {
      setError(saveError.message || "Unable to update user.");
    }
  };

  const handleSuspend = async (user) => {
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/admin/users/${user.id}/suspend`, {
        method: "PATCH",
        body: JSON.stringify({
          reason: user.status === "active" ? "Suspended by admin" : ""
        })
      });
      setSuccess(response?.message || "User status updated.");
      await loadUsers();
    } catch (actionError) {
      setError(actionError.message || "Unable to update user status.");
    }
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(`Delete ${user.fullName || user.email}?`);

    if (!confirmed) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/admin/users/${user.id}`, {
        method: "DELETE"
      });
      setSuccess(response?.message || "User deleted successfully.");
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete user.");
    }
  };

  const handleResetPassword = async () => {
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/admin/users/${resetPasswordTarget.id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ newPassword })
      });
      setSuccess(response?.message || "Password updated successfully.");
      setResetPasswordTarget(null);
      setNewPassword("");
    } catch (resetError) {
      setError(resetError.message || "Unable to reset password.");
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>Users</Typography>
          <Typography sx={{ color: "text.secondary" }}>
            Create and manage customer accounts, then edit, suspend, reactivate, or delete them from one place.
          </Typography>
        </Box>

        {canCreateUsers(adminUser?.role) && (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setIsCreateDialogOpen(true)}>
            New User
          </Button>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5">{users.length}</Typography>
            <Typography sx={{ color: "text.secondary" }}>Total customer accounts</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5">{activeCount}</Typography>
            <Typography sx={{ color: "text.secondary" }}>Active customers</Typography>
          </Paper>
        </Grid>
      </Grid>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={3}>
        {users.map((user) => (
          <Grid item xs={12} md={6} xl={4} key={user.id}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">{user.fullName}</Typography>
                  <Chip
                    label={user.status}
                    color={user.status === "active" ? "success" : "warning"}
                    sx={{ textTransform: "capitalize" }}
                  />
                </Stack>
                <Typography sx={{ color: "text.secondary" }}>{user.email}</Typography>
                <Typography sx={{ color: "text.secondary" }}>{user.phone || "No phone number"}</Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  Joined {new Date(user.createdAt).toLocaleDateString()}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                  {canEditUsers(adminUser?.role) && (
                    <Button variant="outlined" onClick={() => openEditModal(user)}>Edit</Button>
                  )}
                  {canSuspendUsers(adminUser?.role) && (
                    <Button variant="contained" color="warning" onClick={() => handleSuspend(user)}>
                      {user.status === "active" ? "Suspend" : "Reactivate"}
                    </Button>
                  )}
                  {canDeleteUsers(adminUser?.role) && (
                    <Button variant="text" color="error" onClick={() => handleDelete(user)}>
                      Delete
                    </Button>
                  )}
                  {isSuperAdmin(adminUser?.role) && (
                    <Button variant="text" onClick={() => setResetPasswordTarget(user)}>
                      Reset Password
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editingUser) && canEditUsers(adminUser?.role)} onClose={() => setEditingUser(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField label="Full Name" name="fullName" value={editForm.fullName} onChange={handleEditChange} fullWidth />
            <TextField label="Email" name="email" value={editForm.email} onChange={handleEditChange} fullWidth />
            <TextField label="Phone" name="phone" value={editForm.phone} onChange={handleEditChange} fullWidth />
            {isSuperAdmin(adminUser?.role) && (
              <TextField select label="Role" name="role" value={editForm.role} onChange={handleEditChange} fullWidth>
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="stockkeeper">Stockkeeper</MenuItem>
                <MenuItem value="business_manager">Business Manager</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
              </TextField>
            )}
            <TextField select label="Status" name="status" value={editForm.status} onChange={handleEditChange} fullWidth>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
            {editForm.status === "suspended" && (
              <TextField
                label="Suspension Reason"
                name="suspendedReason"
                value={editForm.suspendedReason}
                onChange={handleEditChange}
                fullWidth
                multiline
                minRows={2}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingUser(null)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isCreateDialogOpen && canCreateUsers(adminUser?.role)} onClose={() => setIsCreateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField label="Full Name" name="fullName" value={createForm.fullName} onChange={handleCreateChange} fullWidth />
            <TextField label="Email" name="email" value={createForm.email} onChange={handleCreateChange} fullWidth />
            <TextField label="Phone" name="phone" value={createForm.phone} onChange={handleCreateChange} fullWidth />
            <TextField
              label="Password"
              name="password"
              type="password"
              value={createForm.password}
              onChange={handleCreateChange}
              fullWidth
            />
            <TextField select label="Status" name="status" value={createForm.status} onChange={handleCreateChange} fullWidth>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create User</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetPasswordTarget) && isSuperAdmin(adminUser?.role)} onClose={() => setResetPasswordTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography sx={{ color: "text.secondary" }}>
              Set a new password for {resetPasswordTarget?.fullName || resetPasswordTarget?.email}.
            </Typography>
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordTarget(null)}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained">Update Password</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default UsersPage;
